/**
 * API Service - Servicio principal que combina todos los servicios
 * Mantiene compatibilidad hacia atrás con el archivo original api.ts
 */

import { ApiServiceBase } from './core/ApiService';
import { AuthService } from './services/authService';
import { DocumentService } from './services/documentService';
import { ConversationService } from './services/conversationService';
import { StudioService } from './services/studioService';
import { SavedMessageService } from './services/savedMessageService';
import { SnippetService } from './services/snippetService';
import { AdminService } from './services/adminService';
import { AISettingsService } from './services/aiSettingsService';

// Re-exportar todos los tipos
export * from './types';

// Re-exportar utilidades
export { handleApiError, validateDocumentSelection, validateMessage } from './utils';

/**
 * ApiService - Clase principal que combina todos los servicios
 * Extiende ApiServiceBase y mezcla todos los servicios
 */
class ApiService extends ApiServiceBase {
  // Servicios internos
  private authService: AuthService;
  private documentService: DocumentService;
  private conversationService: ConversationService;
  private studioService: StudioService;
  private savedMessageService: SavedMessageService;
  private snippetService: SnippetService;
  private adminService: AdminService;
  private aiSettingsService: AISettingsService;

  constructor() {
    super();
    // Inicializar servicios
    this.authService = new AuthService();
    this.documentService = new DocumentService();
    this.conversationService = new ConversationService();
    this.studioService = new StudioService();
    this.savedMessageService = new SavedMessageService();
    this.snippetService = new SnippetService();
    this.adminService = new AdminService();
    this.aiSettingsService = new AISettingsService();
  }

  // ========== AUTH METHODS ==========
  async login(...args: Parameters<AuthService['login']>) {
    return this.authService.login(...args);
  }

  async devLogin() {
    return this.authService.devLogin();
  }

  async googleLogin(...args: Parameters<AuthService['googleLogin']>) {
    return this.authService.googleLogin(...args);
  }

  async sendMagicLink(...args: Parameters<AuthService['sendMagicLink']>) {
    return this.authService.sendMagicLink(...args);
  }

  async verifyMagicLink(...args: Parameters<AuthService['verifyMagicLink']>) {
    return this.authService.verifyMagicLink(...args);
  }

  async logout(...args: Parameters<AuthService['logout']>) {
    return this.authService.logout(...args);
  }

  async getProfile(...args: Parameters<AuthService['getProfile']>) {
    return this.authService.getProfile(...args);
  }

  async updateProfile(updates: {
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  }): Promise<{ success: boolean; user?: any; message?: string; error?: string }> {
    return this.request('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
  }

  isAuthenticated(): boolean {
    const user = this.getCurrentUser();
    const token = this.tokenManager.getAccessToken();
    return !!token && !!user;
  }

  // ========== DOCUMENT METHODS ==========
  async getFilteredDocuments(...args: Parameters<DocumentService['getFilteredDocuments']>) {
    return this.documentService.getFilteredDocuments(...args);
  }

  async getDocuments(...args: Parameters<DocumentService['getDocuments']>) {
    return this.documentService.getDocuments(...args);
  }

  async getDocument(...args: Parameters<DocumentService['getDocument']>) {
    return this.documentService.getDocument(...args);
  }

  async uploadDocument(...args: Parameters<DocumentService['uploadDocument']>) {
    return this.documentService.uploadDocument(...args);
  }

  async updateDocument(...args: Parameters<DocumentService['updateDocument']>) {
    return this.documentService.updateDocument(...args);
  }

  async deleteDocument(...args: Parameters<DocumentService['deleteDocument']>) {
    return this.documentService.deleteDocument(...args);
  }

  async getSharedDocuments(...args: Parameters<DocumentService['getSharedDocuments']>) {
    return this.documentService.getSharedDocuments(...args);
  }

  async getSharedFilters(...args: Parameters<DocumentService['getSharedFilters']>) {
    return this.documentService.getSharedFilters(...args);
  }

  async getSharedSectors(...args: Parameters<DocumentService['getSharedSectors']>) {
    return this.documentService.getSharedSectors(...args);
  }

  async getSharedCompaniesList(...args: Parameters<DocumentService['getSharedCompaniesList']>) {
    return this.documentService.getSharedCompaniesList(...args);
  }

  async getSharedRecent(...args: Parameters<DocumentService['getSharedRecent']>) {
    return this.documentService.getSharedRecent(...args);
  }

  async getDocumentSelection(...args: Parameters<DocumentService['getDocumentSelection']>) {
    return this.documentService.getDocumentSelection(...args);
  }

  async saveDocumentSelection(...args: Parameters<DocumentService['saveDocumentSelection']>) {
    return this.documentService.saveDocumentSelection(...args);
  }

  async reprocessDocument(...args: Parameters<DocumentService['reprocessDocument']>) {
    return this.documentService.reprocessDocument(...args);
  }

  async processDocument(...args: Parameters<DocumentService['processDocument']>) {
    return this.documentService.processDocument(...args);
  }

  async getCompanies(...args: Parameters<DocumentService['getCompanies']>) {
    return this.documentService.getCompanies(...args);
  }

  async getMentionConfig(...args: Parameters<DocumentService['getMentionConfig']>) {
    return this.documentService.getMentionConfig(...args);
  }

  async getMentionSuggestions(...args: Parameters<DocumentService['getMentionSuggestions']>) {
    return this.documentService.getMentionSuggestions(...args);
  }

  async searchFiles(...args: Parameters<DocumentService['searchFiles']>) {
    return this.documentService.searchFiles(...args);
  }

  async getChunkWithContext(...args: Parameters<DocumentService['getChunkWithContext']>) {
    return this.documentService.getChunkWithContext(...args);
  }

  async getDocumentChunks(...args: Parameters<DocumentService['getDocumentChunks']>) {
    return this.documentService.getDocumentChunks(...args);
  }

  async getChunkTables(...args: Parameters<DocumentService['getChunkTables']>) {
    return this.documentService.getChunkTables(...args);
  }

  async getDocumentTables(...args: Parameters<DocumentService['getDocumentTables']>) {
    return this.documentService.getDocumentTables(...args);
  }

  async getUnstructuredPayload(...args: Parameters<DocumentService['getUnstructuredPayload']>) {
    return this.documentService.getUnstructuredPayload(...args);
  }

  async getUnstructuredTables(...args: Parameters<DocumentService['getUnstructuredTables']>) {
    return this.documentService.getUnstructuredTables(...args);
  }

  // ========== CONVERSATION METHODS ==========
  async getConversations(...args: Parameters<ConversationService['getConversations']>) {
    return this.conversationService.getConversations(...args);
  }

  async createConversation(...args: Parameters<ConversationService['createConversation']>) {
    return this.conversationService.createConversation(...args);
  }

  async getConversation(...args: Parameters<ConversationService['getConversation']>) {
    return this.conversationService.getConversation(...args);
  }

  async updateConversation(...args: Parameters<ConversationService['updateConversation']>) {
    return this.conversationService.updateConversation(...args);
  }

  async deleteConversation(...args: Parameters<ConversationService['deleteConversation']>) {
    return this.conversationService.deleteConversation(...args);
  }

  async duplicateConversation(...args: Parameters<ConversationService['duplicateConversation']>) {
    return this.conversationService.duplicateConversation(...args);
  }

  async searchConversations(...args: Parameters<ConversationService['searchConversations']>) {
    return this.conversationService.searchConversations(...args);
  }

  async preSelectDocuments(...args: Parameters<ConversationService['preSelectDocuments']>) {
    return this.conversationService.preSelectDocuments(...args);
  }

  async sendMessage(...args: Parameters<ConversationService['sendMessage']>) {
    return this.conversationService.sendMessage(...args);
  }

  async sendMessageStream(...args: Parameters<ConversationService['sendMessageStream']>) {
    return this.conversationService.sendMessageStream(...args);
  }

  async rateMessage(...args: Parameters<ConversationService['rateMessage']>) {
    return this.conversationService.rateMessage(...args);
  }

  async clearConversationContext(
    ...args: Parameters<ConversationService['clearConversationContext']>
  ) {
    return this.conversationService.clearConversationContext(...args);
  }

  async deleteConversationMessages(
    ...args: Parameters<ConversationService['deleteConversationMessages']>
  ) {
    return this.conversationService.deleteConversationMessages(...args);
  }

  async deleteMessage(...args: Parameters<ConversationService['deleteMessage']>) {
    return this.conversationService.deleteMessage(...args);
  }

  async toggleMessageContext(...args: Parameters<ConversationService['toggleMessageContext']>) {
    return this.conversationService.toggleMessageContext(...args);
  }

  async createMessage(...args: Parameters<ConversationService['createMessage']>) {
    return this.conversationService.createMessage(...args);
  }

  async saveMessage(...args: Parameters<ConversationService['saveMessage']>) {
    return this.conversationService.saveMessage(...args);
  }

  async unsaveMessage(...args: Parameters<ConversationService['unsaveMessage']>) {
    return this.conversationService.unsaveMessage(...args);
  }

  async editMessage(...args: Parameters<ConversationService['editMessage']>) {
    return this.conversationService.editMessage(...args);
  }

  async getConfigInfo(...args: Parameters<ConversationService['getConfigInfo']>) {
    return this.conversationService.getConfigInfo(...args);
  }

  async updateConfigQuick(...args: Parameters<ConversationService['updateConfigQuick']>) {
    return this.conversationService.updateConfigQuick(...args);
  }

  // ========== STUDIO METHODS ==========
  async getAISuggestedQuestions(...args: Parameters<StudioService['getAISuggestedQuestions']>) {
    return this.studioService.getAISuggestedQuestions(...args);
  }

  async getAISuggestedTasks(...args: Parameters<StudioService['getAISuggestedTasks']>) {
    return this.studioService.getAISuggestedTasks(...args);
  }

  async healthCheck(...args: Parameters<StudioService['healthCheck']>) {
    return this.studioService.healthCheck(...args);
  }

  // ========== AI SETTINGS METHODS ==========
  async getAIConfig(...args: Parameters<AISettingsService['getAIConfig']>) {
    return this.aiSettingsService.getAIConfig(...args);
  }

  async getAISettings(...args: Parameters<AISettingsService['getAISettings']>) {
    return this.aiSettingsService.getAISettings(...args);
  }

  async updateAISettings(...args: Parameters<AISettingsService['updateAISettings']>) {
    return this.aiSettingsService.updateAISettings(...args);
  }

  async applyAIPreset(...args: Parameters<AISettingsService['applyAIPreset']>) {
    return this.aiSettingsService.applyAIPreset(...args);
  }

  async resetAISettings(...args: Parameters<AISettingsService['resetAISettings']>) {
    return this.aiSettingsService.resetAISettings(...args);
  }

  // ========== SAVED MESSAGE METHODS ==========
  async getSavedMessages(...args: Parameters<SavedMessageService['getSavedMessages']>) {
    return this.savedMessageService.getSavedMessages(...args);
  }

  async saveChatMessage(...args: Parameters<SavedMessageService['saveChatMessage']>) {
    return this.savedMessageService.saveChatMessage(...args);
  }

  async updateSavedMessage(...args: Parameters<SavedMessageService['updateSavedMessage']>) {
    return this.savedMessageService.updateSavedMessage(...args);
  }

  async deleteSavedMessage(...args: Parameters<SavedMessageService['deleteSavedMessage']>) {
    return this.savedMessageService.deleteSavedMessage(...args);
  }

  async reorderSavedMessages(...args: Parameters<SavedMessageService['reorderSavedMessages']>) {
    return this.savedMessageService.reorderSavedMessages(...args);
  }

  // ========== SNIPPET METHODS ==========
  async getPDFSnippets(...args: Parameters<SnippetService['getPDFSnippets']>) {
    return this.snippetService.getPDFSnippets(...args);
  }

  async getPDFSnippet(...args: Parameters<SnippetService['getPDFSnippet']>) {
    return this.snippetService.getPDFSnippet(...args);
  }

  async createPDFSnippet(...args: Parameters<SnippetService['createPDFSnippet']>) {
    return this.snippetService.createPDFSnippet(...args);
  }

  async updatePDFSnippet(...args: Parameters<SnippetService['updatePDFSnippet']>) {
    return this.snippetService.updatePDFSnippet(...args);
  }

  async deletePDFSnippet(...args: Parameters<SnippetService['deletePDFSnippet']>) {
    return this.snippetService.deletePDFSnippet(...args);
  }

  // ========== ADMIN METHODS ==========
  async uploadAdminDocument(...args: Parameters<AdminService['uploadAdminDocument']>) {
    return this.adminService.uploadAdminDocument(...args);
  }

  async getAdminDocuments(...args: Parameters<AdminService['getAdminDocuments']>) {
    return this.adminService.getAdminDocuments(...args);
  }

  async deleteAllDocuments(...args: Parameters<AdminService['deleteAllDocuments']>) {
    return this.adminService.deleteAllDocuments(...args);
  }

  async bulkDeleteDocuments(...args: Parameters<AdminService['bulkDeleteDocuments']>) {
    return this.adminService.bulkDeleteDocuments(...args);
  }
}

// Instancia singleton del servicio API
export const apiService = new ApiService();

// Hook personalizado para React
export const useApi = () => {
  return apiService;
};

// Re-exportar studioRegistry para compatibilidad
export { studioRegistry } from '../studioRegistry';

// Extender ApiService con métodos de Studio Registry (para compatibilidad)
declare module './index' {
  interface ApiService {
    getStudioModules(): Promise<any[]>;
    generateStudioContent(studioId: string, request: any): Promise<any>;
    createStudio(request: any): Promise<any>;
    updateStudio(studioId: string, request: any): Promise<any>;
    deleteStudio(studioId: string): Promise<void>;
    clearStudioCache(studioId: string): Promise<void>;
  }
}

export default apiService;
